import { useCallback } from 'react'
import type { TodoPriority } from '../api/types'
import { useUpdateTodo } from '../api/hooks'

const PRIORITIES: { value: TodoPriority | null; label: string; cssVar: string | null }[] = [
  { value: 'high', label: 'High priority', cssVar: 'var(--color-prio-high)' },
  { value: 'medium', label: 'Medium priority', cssVar: 'var(--color-prio-medium)' },
  { value: 'low', label: 'Low priority', cssVar: 'var(--color-prio-low)' },
  { value: null, label: 'No priority', cssVar: null },
]

export type PriorityPickerProps = {
  todoId: string
  currentPriority: TodoPriority | null
  onApplied?: () => void
}

/**
 * Row of priority dots for use inside a context menu or panel.
 */
export function PriorityPicker({ todoId, currentPriority, onApplied }: PriorityPickerProps) {
  const updateTodo = useUpdateTodo()

  const select = useCallback(
    (value: TodoPriority | null) => {
      updateTodo.mutate(
        {
          id: todoId,
          priority: value === null ? '' : value,
        },
        { onSuccess: onApplied }
      )
    },
    [todoId, updateTodo, onApplied]
  )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'center',
      }}
    >
      {PRIORITIES.map(({ value, label, cssVar }) => {
        const active = value === currentPriority || (value === null && currentPriority === null)
        return (
          <button
            key={value ?? 'none'}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              select(value)
            }}
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              padding: 0,
              flexShrink: 0,
              cursor: 'pointer',
              border: '1.5px solid var(--color-ink-muted)',
              background: cssVar ?? 'transparent',
              boxShadow: active ? '0 0 0 2px var(--color-accent)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {value === null ? (
              <span
                aria-hidden
                style={{
                  width: '70%',
                  height: 1.5,
                  background: 'var(--color-ink-muted)',
                  transform: 'rotate(-45deg)',
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
